// OWNED BY the documents implementation agent (see ../DocumentsTab.tsx ownership note).
//
// One uploaded-document row (mock s19 ct-row): mime icon, name + meta line,
// "חדש" label for fresh uploads, download, and a more-menu with inline rename,
// group move (מסמכי לקוח ⇄ אחר) and delete (confirm + best-effort storage
// object cleanup).

import { useRef, useState } from "react";
import {
  Box,
  Menu,
  Stack,
  Button,
  Dialog,
  MenuItem,
  TextField,
  IconButton,
  Typography,
  DialogTitle,
  DialogActions,
  DialogContent,
  ListItemIcon,
  ListItemText,
  DialogContentText,
  CircularProgress
} from "@material-ui/core";
import { alpha } from "@material-ui/core/styles";
import {
  Edit,
  GetApp,
  MoreVert,
  SwapHoriz,
  DeleteOutline,
  Image as ImageIcon,
  PictureAsPdf,
  Description,
  InsertDriveFile
} from "@material-ui/icons";
import Label from "components/Label";
import useAuth from "hooks/useAuth";
import { useSnackbar } from "notistack5";
import {
  fileKindOf,
  formatDocDate,
  isRecentUpload,
  uploaderLabel,
  formatFileSize,
  fileExtensionLabel,
  renameContactDocument,
  deleteContactDocument,
  downloadContactDocumentFile,
  setContactDocumentCategory
} from "./documentsData";
import type { FileKind, ContactDocumentWithId } from "./documentsData";

const KIND_ICON: Record<FileKind, typeof PictureAsPdf> = {
  pdf: PictureAsPdf,
  image: ImageIcon,
  word: Description,
  other: InsertDriveFile
};

const KIND_COLOR: Record<FileKind, "error" | "info" | "primary" | "warning"> = {
  pdf: "primary",
  image: "info",
  word: "info",
  other: "warning"
};

type Props = {
  contactId: string;
  doc: ContactDocumentWithId;
};

export function DocumentRow({ contactId, doc }: Props): JSX.Element {
  const { user } = useAuth();
  const { enqueueSnackbar } = useSnackbar();

  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [renameValue, setRenameValue] = useState(doc.name);
  const renameBusyRef = useRef(false);

  const kind = fileKindOf(doc.mimeType, doc.name);
  const KindIcon = KIND_ICON[kind];
  const kindColor = KIND_COLOR[kind];

  const metaParts = [
    `הועלה ${formatDocDate(doc.uploadedAt)} ע״י ${uploaderLabel(
      doc.uploadedByEmail,
      user?.email,
      user?.displayName
    )}`,
    fileExtensionLabel(doc.name, doc.mimeType),
    formatFileSize(doc.sizeBytes)
  ].filter(Boolean);

  const closeMenu = (): void => setMenuAnchor(null);

  const startRename = (): void => {
    closeMenu();
    setRenameValue(doc.name);
    setRenaming(true);
  };

  const commitRename = async (): Promise<void> => {
    if (renameBusyRef.current) return;
    const newName = renameValue.trim();
    setRenaming(false);
    if (!newName || newName === doc.name) return;
    renameBusyRef.current = true;
    try {
      await renameContactDocument(contactId, doc, newName);
    } catch (error) {
      console.error("Documents tab: rename failed", error);
      enqueueSnackbar("שינוי שם המסמך נכשל", { variant: "error", autoHideDuration: 4000 });
    } finally {
      renameBusyRef.current = false;
    }
  };

  const handleMoveGroup = async (): Promise<void> => {
    closeMenu();
    const nextCategory = doc.category === "other" ? "customerDoc" : "other";
    try {
      await setContactDocumentCategory(contactId, doc, nextCategory);
    } catch (error) {
      console.error("Documents tab: category move failed", error);
      enqueueSnackbar("העברת המסמך נכשלה", { variant: "error", autoHideDuration: 4000 });
    }
  };

  const handleDownload = async (): Promise<void> => {
    setDownloading(true);
    try {
      await downloadContactDocumentFile(doc);
    } catch (error) {
      console.error("Documents tab: download failed", error);
      enqueueSnackbar("הורדת הקובץ נכשלה", { variant: "error", autoHideDuration: 4000 });
    } finally {
      setDownloading(false);
    }
  };

  const handleDelete = async (): Promise<void> => {
    setDeleting(true);
    try {
      await deleteContactDocument(contactId, doc);
      setConfirmingDelete(false);
    } catch (error) {
      console.error("Documents tab: delete failed", error);
      enqueueSnackbar("מחיקת המסמך נכשלה", { variant: "error", autoHideDuration: 4000 });
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Stack
      direction="row"
      alignItems="center"
      gap={1.5}
      sx={{
        px: 1,
        py: 1.25,
        borderRadius: 1,
        "&:hover": { bgcolor: "action.hover" }
      }}
    >
      <Box
        sx={{
          width: 40,
          height: 40,
          flexShrink: 0,
          borderRadius: 1.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: `${kindColor}.dark`,
          bgcolor: (theme) => alpha(theme.palette[kindColor].main, 0.12)
        }}
      >
        <KindIcon fontSize="small" />
      </Box>

      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
        {renaming ? (
          <TextField
            autoFocus
            fullWidth
            size="small"
            variant="standard"
            value={renameValue}
            inputProps={{ dir: "auto", "aria-label": "שם המסמך" }}
            onChange={(event) => setRenameValue(event.target.value)}
            onBlur={() => void commitRename()}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                void commitRename();
              } else if (event.key === "Escape") {
                setRenameValue(doc.name);
                setRenaming(false);
              }
            }}
          />
        ) : (
          <Typography variant="subtitle2" noWrap dir="auto">
            {doc.name}
          </Typography>
        )}
        <Typography variant="caption" noWrap component="div" sx={{ color: "text.secondary" }}>
          {metaParts.join(" · ")}
        </Typography>
      </Box>

      {isRecentUpload(doc.uploadedAt) && <Label color="success">חדש</Label>}

      <IconButton
        size="small"
        aria-label="הורדת הקובץ"
        disabled={downloading}
        onClick={() => void handleDownload()}
      >
        {downloading ? <CircularProgress size={18} /> : <GetApp fontSize="small" />}
      </IconButton>

      <IconButton
        size="small"
        aria-label="עוד פעולות לקובץ"
        onClick={(event) => setMenuAnchor(event.currentTarget)}
      >
        <MoreVert fontSize="small" />
      </IconButton>

      <Menu anchorEl={menuAnchor} open={!!menuAnchor} onClose={closeMenu}>
        <MenuItem onClick={startRename}>
          <ListItemIcon sx={{ minWidth: 32 }}>
            <Edit fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="שינוי שם" />
        </MenuItem>
        <MenuItem onClick={() => void handleMoveGroup()}>
          <ListItemIcon sx={{ minWidth: 32 }}>
            <SwapHoriz fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary={doc.category === "other" ? "העברה למסמכי לקוח" : "העברה לקבוצת ״אחר״"}
          />
        </MenuItem>
        <MenuItem
          sx={{ color: "error.main" }}
          onClick={() => {
            closeMenu();
            setConfirmingDelete(true);
          }}
        >
          <ListItemIcon sx={{ minWidth: 32, color: "error.main" }}>
            <DeleteOutline fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="מחיקה" />
        </MenuItem>
      </Menu>

      <Dialog
        open={confirmingDelete}
        maxWidth="xs"
        fullWidth
        onClose={() => !deleting && setConfirmingDelete(false)}
      >
        <DialogTitle>מחיקת מסמך</DialogTitle>
        <DialogContent>
          <DialogContentText dir="auto">
            למחוק את ״{doc.name}״? הקובץ יימחק לצמיתות.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button disabled={deleting} onClick={() => setConfirmingDelete(false)}>
            ביטול
          </Button>
          <Button
            color="error"
            variant="contained"
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={16} /> : <DeleteOutline />}
            onClick={() => void handleDelete()}
          >
            מחיקה
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
